import React, { useState, useEffect } from 'react';
import Icon from '../Shared/Icons';
import { db } from '../../config/firebase';
import {
  collection, getDocs, query,
  where, deleteDoc, doc
} from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Sidebar from '../Shared/Sidebar';
import { DRAFT_TYPES, getDraftConfig } from '../../utils/draftUtils';

export default function Drafts() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchDrafts();
  }, []);

  async function fetchDrafts() {
    try {
      const q = query(
        collection(db, 'drafts'),
        where('createdBy', '==', currentUser.uid)
      );
      const snap = await getDocs(q);
      const data = snap.docs.map(d => ({
        id: d.id, ...d.data()
      }));
      data.sort((a, b) => {
        const aTime = a.updatedAt?.seconds || 0;
        const bTime = b.updatedAt?.seconds || 0;
        return bTime - aTime;
      });
      setDrafts(data);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }

  async function handleDelete(id) {
    if (window.confirm(
      'Are you sure you want to delete this draft?'
    )) {
      await deleteDoc(doc(db, 'drafts', id));
      fetchDrafts();
    }
  }

  function handleContinue(draft) {
    const cfg = getDraftConfig(draft.type);
    navigate(cfg.route, { state: { draft } });
  }

  function getCompletionColor(pct) {
    if (pct >= 80) return '#14452F';
    if (pct >= 50) return '#F57C00';
    return '#C62828';
  }

  function getCompletionLabel(pct) {
    if (pct >= 80) return 'Almost Complete';
    if (pct >= 50) return 'In Progress';
    return 'Just Started';
  }

  const filtered = drafts.filter(d => {
    if (filter === 'all') return true;
    return d.type === filter;
  });

  return (
    <div style={styles.layout}>
      <Sidebar />
      <div style={styles.main}>

        <div style={styles.pageHeader}>
          <div>
            <div style={styles.pageTitle}>
              Incomplete Registrations
            </div>
            <div style={styles.pageSub}>
              Continue where you left off
            </div>
          </div>
          <div style={styles.headerBtns}>
            <button
              style={styles.btnPrimary}
              onClick={() => navigate('/staff/add-student')}
            >
              + New Student
            </button>
            <button
              style={styles.btnSecondary}
              onClick={() => navigate('/staff/add-parent')}
            >
              + New Parent
            </button>
          </div>
        </div>

        {/* STATS */}
        <div style={styles.statsRow}>
          <div style={styles.statCard}>
            <div style={styles.statIcon}></div>
            <div style={{ ...styles.statVal, color: '#14452F' }}>{drafts.length}</div>
            <div style={styles.statLbl}>Total Drafts</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statIcon}></div>
            <div style={{ ...styles.statVal, color: '#00695C' }}>
              {drafts.filter(d => (d.completion || 0) >= 80).length}
            </div>
            <div style={styles.statLbl}>Almost Complete</div>
          </div>
          {Object.entries(DRAFT_TYPES).map(([key, cfg]) => {
            const count = drafts.filter(d => d.type === key).length;
            if (count === 0) return null;
            return (
              <div key={key} style={styles.statCard}>
                <div style={styles.statIcon}><Icon name={cfg.icon} size={18} color={cfg.color} /></div>
                <div style={{ ...styles.statVal, color: cfg.color }}>{count}</div>
                <div style={styles.statLbl}>{cfg.label} Drafts</div>
              </div>
            );
          })}
        </div>

        {/* FILTERS */}
        <div style={styles.filterRow}>
          <button
            style={{ ...styles.filterBtn, ...(filter === 'all' ? styles.filterActive : {}) }}
            onClick={() => setFilter('all')}
          >
            All Drafts
          </button>
          {Object.entries(DRAFT_TYPES).map(([key, cfg]) => (
            <button
              key={key}
              style={{ ...styles.filterBtn, ...(filter === key ? styles.filterActive : {}) }}
              onClick={() => setFilter(key)}
            >
              <Icon name={cfg.icon} size={18} color={cfg.color} /> {cfg.label}
            </button>
          ))}
        </div>

        {/* LIST */}
        {loading ? (
          <div style={styles.emptyCard}>
            <div style={styles.emptyIcon}></div>
            <div style={styles.emptyText}>Loading drafts...</div>
          </div>
        ) : filtered.length === 0 ? (
          <div style={styles.emptyCard}>
            <div style={styles.emptyIcon}></div>
            <div style={styles.emptyTitle}>
              No Incomplete Registrations
            </div>
            <div style={styles.emptyText}>
              All registrations are complete!
            </div>
            <div style={styles.emptyBtns}>
              <button
                style={styles.btnPrimary}
                onClick={() => navigate('/staff/add-student')}
              >
                + Register Student
              </button>
              <button
                style={styles.btnSecondary}
                onClick={() => navigate('/staff/add-parent')}
              >
                + Register Parent
              </button>
            </div>
          </div>
        ) : (
          <div style={styles.draftsList}>
            {filtered.map(draft => {
              const cfg = getDraftConfig(draft.type);
              const displayName =
                draft.data?.firstName && draft.data?.lastName
                  ? `${draft.data.firstName} ${draft.data.lastName}`
                  : draft.data?.firstName || draft.data?.title || draft.data?.name
                  || 'Not named yet';
              return (
                <div key={draft.id} style={styles.draftCard}>
                  <div style={styles.draftHeader}>
                    <div style={{
                      ...styles.draftAvatar,
                      background: `linear-gradient(135deg, ${cfg.color}, ${cfg.color}CC)`
                    }}>
                      <Icon name={cfg.icon} size={18} color={cfg.color} />
                    </div>
                    <div style={styles.draftInfo}>
                      <div style={styles.draftName}>{displayName}</div>
                      <div style={styles.draftMeta}><Icon name={cfg.icon} size={18} color={cfg.color} /> {cfg.label}</div>
                    </div>
                    <div style={styles.draftActions}>
                      <button
                        style={styles.continueBtn}
                        onClick={() => handleContinue(draft)}
                      >
                        Continue
                      </button>
                      <button
                        style={styles.deleteBtn}
                        onClick={() => handleDelete(draft.id)}
                      >
                        Futa
                      </button>
                    </div>
                  </div>

                  <div style={styles.progressSection}>
                    <div style={styles.progressHeader}>
                      <span style={styles.progressLabel}>Completion</span>
                      <span style={{
                        ...styles.progressValue,
                        color: getCompletionColor(draft.completion || 0)
                      }}>
                        {draft.completion || 0}% — {getCompletionLabel(draft.completion || 0)}
                      </span>
                    </div>
                    <div style={styles.progressBg}>
                      <div style={{
                        ...styles.progressFill,
                        width: `${draft.completion || 0}%`,
                        background: getCompletionColor(draft.completion || 0)
                      }} />
                    </div>
                  </div>

                  <div style={styles.fieldsRow}>
                    {cfg.previewFields.map((field, i) => (
                      <span key={i} style={{
                        ...styles.fieldBadge,
                        ...(draft.data?.[field] ? styles.fieldDone : styles.fieldMissing)
                      }}>
                        {draft.data?.[field] ? '' : '○'} {field}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  layout: { display: 'flex', minHeight: '100vh', background: '#F6F4EE' },
  main: { flex: 1, padding: '26px', overflowY: 'auto' },
  pageHeader: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'flex-start', marginBottom: '24px',
    flexWrap: 'wrap', gap: '14px',
  },
  pageTitle: {
    fontFamily: "'Fraunces', Georgia, serif", fontSize: '25px',
    background: 'linear-gradient(135deg, #14452F, #1F6B45)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
  },
  pageSub: { fontSize: '12.5px', color: '#6B7280', marginTop: '3px' },
  headerBtns: { display: 'flex', gap: '10px' },
  btnPrimary: {
    background: 'linear-gradient(135deg, #14452F, #1F6B45)',
    color: '#fff', border: 'none', padding: '10px 18px',
    borderRadius: '10px', fontFamily: 'inherit', fontSize: '13px',
    fontWeight: '600', cursor: 'pointer',
  },
  btnSecondary: {
    background: 'linear-gradient(135deg, #F57C00, #FFA726)',
    color: '#fff', border: 'none', padding: '10px 18px',
    borderRadius: '10px', fontFamily: 'inherit', fontSize: '13px',
    fontWeight: '600', cursor: 'pointer',
  },
  statsRow: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
    gap: '12px', marginBottom: '20px',
  },
  statCard: {
    background: '#fff', borderRadius: '14px', padding: '16px',
    textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
  },
  statIcon: { fontSize: '22px', marginBottom: '6px' },
  statVal: { fontFamily: "'Fraunces', Georgia, serif", fontSize: '26px', fontWeight: '700' },
  statLbl: { fontSize: '11px', color: '#6B7280', marginTop: '2px' },
  filterRow: { display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' },
  filterBtn: {
    padding: '8px 16px', borderRadius: '20px', border: '1.5px solid #E5E7EB',
    background: 'transparent', color: '#6B7280', fontSize: '12.5px',
    cursor: 'pointer', fontFamily: 'inherit',
  },
  filterActive: {
    background: 'linear-gradient(135deg, #14452F, #1F6B45)',
    borderColor: 'transparent', color: '#fff',
  },
  emptyCard: {
    background: '#fff', borderRadius: '20px', padding: '60px 32px',
    textAlign: 'center', boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
  },
  emptyIcon: { fontSize: '56px', marginBottom: '16px' },
  emptyTitle: { fontFamily: "'Fraunces', Georgia, serif", fontSize: '20px', color: '#1C1C1E', marginBottom: '8px' },
  emptyText: { fontSize: '13.5px', color: '#6B7280', lineHeight: 1.6, marginBottom: '24px' },
  emptyBtns: { display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' },
  draftsList: { display: 'flex', flexDirection: 'column', gap: '14px' },
  draftCard: {
    background: '#fff', borderRadius: '16px', padding: '18px 20px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
  },
  draftHeader: {
    display: 'flex', alignItems: 'center', gap: '14px',
    marginBottom: '14px', flexWrap: 'wrap',
  },
  draftAvatar: {
    width: '44px', height: '44px', borderRadius: '12px',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '20px', flexShrink: 0,
  },
  draftInfo: { flex: 1, minWidth: '140px' },
  draftName: { fontSize: '15px', fontWeight: '600', color: '#1C1C1E', marginBottom: '3px' },
  draftMeta: { fontSize: '12px', color: '#6B7280' },
  draftActions: { display: 'flex', gap: '8px', alignItems: 'center' },
  continueBtn: {
    background: 'linear-gradient(135deg, #14452F, #1F6B45)',
    color: '#fff', border: 'none', padding: '8px 16px',
    borderRadius: '9px', fontSize: '13px', fontWeight: '600',
    cursor: 'pointer', fontFamily: 'inherit',
  },
  deleteBtn: {
    background: '#FFEBEE', color: '#C62828', border: 'none',
    padding: '8px 10px', borderRadius: '9px', fontSize: '14px',
    cursor: 'pointer', fontFamily: 'inherit',
  },
  progressSection: { marginBottom: '12px' },
  progressHeader: { display: 'flex', justifyContent: 'space-between', marginBottom: '6px' },
  progressLabel: { fontSize: '12px', color: '#6B7280', fontWeight: '500' },
  progressValue: { fontSize: '12px', fontWeight: '600' },
  progressBg: { height: '8px', background: '#E5E7EB', borderRadius: '99px', overflow: 'hidden' },
  progressFill: { height: '100%', borderRadius: '99px', transition: 'width 0.3s' },
  fieldsRow: { display: 'flex', gap: '6px', flexWrap: 'wrap' },
  fieldBadge: { fontSize: '11px', padding: '3px 10px', borderRadius: '20px', fontWeight: '500' },
  fieldDone: { background: '#EAF3EC', color: '#14452F' },
  fieldMissing: { background: '#F3F4F6', color: '#9CA3AF' },
};